import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const InformationCentrePage = () => {
  return (
    <div>
      <SEO
        title="Information Centre | Septic Guides, FAQs & Cost Tools"
        description="Explore Groundhogs Septic Systems' information centre: cost guides, FAQs, blog articles, service areas and tools to help you plan septic installation in Northwestern Ontario."
        keywords="septic information centre, septic guides, Thunder Bay septic blog, septic FAQ, septic cost calculator"
      />
      <Header />

      {/* Hero */}
      <section className="gh-intro pt-32 pb-16 bg-gradient-to-br from-orange-50 to-white">
        <div className="gh-page-heading max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-reveal>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Septic Information Centre
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-10">
            Cost guides, FAQs, blog articles and tools to help homeowners across
            Northwestern Ontario plan septic installation, replacement and
            maintenance with confidence.
          </p>
          <div className="max-w-2xl mx-auto pt-6 mt-6 border-t border-orange-100">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/blog"
                className="inline-flex items-center justify-center px-6 py-3 rounded-md font-semibold text-white shadow-md hover:opacity-90"
                style={{ backgroundColor: "var(--primary-orange)" }}
              >
                <span>Browse All Guides &amp; Articles</span>
              </Link>
              <span className="text-sm text-gray-500 mt-2 sm:mt-0">
                Deep-dive blog content on septic installation, maintenance &amp; costs.
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Main grid */}
      <section className="py-16 bg-white">

      {/* Thunder Bay Hub CTA (internal link to avoid orphan page) */}
      <section className="py-10 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-orange-100 rounded-lg shadow-sm p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold" style={{ color: "var(--dark-navy)" }}>
                Thunder Bay Septic Systems
              </h2>
              <p className="mt-2 text-sm sm:text-base text-gray-600 max-w-2xl">
                Looking for a local starting point? Explore our Thunder Bay hub for septic installation and replacement, plus links to the most relevant guides.
              </p>
            </div>
            <Link
              to="/thunder-bay/septic-systems"
              className="inline-flex items-center justify-center px-6 py-3 rounded-md font-semibold text-white hover:opacity-90"
              style={{ backgroundColor: "var(--primary-orange)" }}
            >
              View the Thunder Bay Hub
            </Link>
          </div>
        </div>
      </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-10 lg:grid-cols-3">
          {/* Column 1: Cost & tools */}
          <div className="space-y-6">
            <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--dark-navy)" }}>
                Cost Guides & Calculator
              </h2>
              <p className="text-gray-600 mb-4">
                Understand what septic systems cost in Thunder Bay and across
                Northwestern Ontario, then use our calculator for ballpark
                pricing.
              </p>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link
                    to="/blog/septic-installation-cost-thunder-bay-2025"
                    className="text-orange-600 hover:underline"
                  >
                    Septic Installation Cost Guide  Thunder Bay (2025)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/guides/septic-system-cost-ontario"
                    className="text-orange-600 hover:underline"
                  >
                    Septic System Cost Ontario (2025 Guide)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/guides/septic-system-replacement-cost-thunder-bay"
                    className="text-orange-600 hover:underline"
                  >
                    Septic System Replacement Cost Thunder Bay (2025)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/guides/septic-tank-installation-cost-ontario"
                    className="text-orange-600 hover:underline"
                  >
                    Septic Tank Installation Cost Ontario (2025 Guide)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog/septic-tank-vs-septic-field-difference"
                    className="text-orange-600 hover:underline"
                  >
                    Septic Tank vs. Septic Field  Costs & Differences
                  </Link>
                </li>
                <li>
                  <Link
                    to="/septic-calculator"
                    className="inline-block mt-3 px-4 py-2 rounded-md font-semibold text-white hover:opacity-90"
                    style={{ backgroundColor: "var(--primary-orange)" }}
                  >
                    Open Septic Cost Calculator
                  </Link>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--dark-navy)" }}>
                Permit & Design Resources
              </h2>
              <p className="text-gray-600 mb-4">
                Learn how septic permits, percolation tests and engineered
                designs work in Ontario.
              </p>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link
                    to="/blog/septic-permit-process-thunder-bay-complete-guide"
                    className="text-orange-600 hover:underline"
                  >
                    Septic Permit Process in Thunder Bay
                  </Link>
                </li>
                <li>
                  <Link
                    to="/septic-system-design"
                    className="text-orange-600 hover:underline"
                  >
                    Septic System Design Services
                  </Link>
                </li>
                <li>
                  <Link
                    to="/guides/percolation-test-thunder-bay-ontario"
                    className="text-orange-600 hover:underline"
                  >
                    Percolation Test Thunder Bay / Ontario (2025 Guide)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog/septic-system-design-thunder-bay"
                    className="text-orange-600 hover:underline"
                  >
                    Septic System Design Guide – Thunder Bay
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/eljen-mound-installation"
                    className="text-orange-600 hover:underline"
                  >
                    Eljen Mound Installation Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/faq"
                    className="text-orange-600 hover:underline"
                  >
                    Septic FAQ
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Column 2: Blog & education */}
          <div className="space-y-6">
            <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--dark-navy)" }}>
                Guides & Articles
              </h2>
              <p className="text-gray-600 mb-4">
                Deep-dive articles on installation options, winter care,
                system comparisons and more.
              </p>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link
                    to="/guides/eljen-gsf-system-ontario"
                    className="text-orange-600 hover:underline"
                  >
                    Eljen GSF System – Ontario Guide (2025)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog/traditional-vs-eljen-septic-systems-thunder-bay"
                    className="text-orange-600 hover:underline"
                  >
                    Traditional vs. Eljen Systems
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog/septic-system-winter-care-thunder-bay"
                    className="text-orange-600 hover:underline"
                  >
                    Septic System Winter Care
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog/septic-tank-inspection-checklist-obc-guide"
                    className="text-orange-600 hover:underline"
                  >
                    Septic Tank Inspection Checklist (OBC Guide)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog/systemized-septic-installation-approach-groundhogs"
                    className="text-orange-600 hover:underline"
                  >
                    Systemized Septic Installation Approach
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog/septic-system-installer-near-me-thunder-bay"
                    className="text-orange-600 hover:underline"
                  >
                    Septic System Installer Near Me  Thunder Bay
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog/septic-system-replacement-cost-ontario-2025"
                    className="text-orange-600 hover:underline"
                  >
                    Septic System Replacement Cost in Ontario (2025)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog/septic-field-repair-vs-replacement-ontario"
                    className="text-orange-600 hover:underline"
                  >
                    Septic Field Repair vs Replacement in Ontario
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog"
                    className="inline-block mt-3 px-4 py-2 rounded-md font-semibold border border-orange-500 text-orange-600 hover:bg-orange-50"
                  >
                    View All Articles
                  </Link>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--dark-navy)" }}>
                Real Estate & Partner Resources
              </h2>
              <p className="text-gray-600 mb-4">
                Resources for agents, inspectors and homeowners dealing with
                septic, foundation and drainage issues.
              </p>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link
                    to="/blog/why-real-estate-professionals-recommend-groundhogs"
                    className="text-orange-600 hover:underline"
                  >
                    Why Real Estate Pros Recommend Groundhogs
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog/basement-waterproofing-foundation-repair-groundhogs"
                    className="text-orange-600 hover:underline"
                  >
                    Basement Waterproofing &amp; Foundation Repair (Groundhogs.ca)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/divisions"
                    className="text-orange-600 hover:underline"
                  >
                    Groundhogs Divisions Overview
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Column 3: Service areas & getting started */}
          <div className="space-y-6">
            <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--dark-navy)" }}>
                Service Areas & Local Guides
              </h2>
              <p className="text-gray-600 mb-4">
                See where we install septic systems and read location-specific
                information.
              </p>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link
                    to="/service-areas/thunder-bay"
                    className="text-orange-600 hover:underline"
                  >
                    Septic Installation in Thunder Bay
                  </Link>
                </li>
                <li>
                  <Link
                    to="/service-areas/kenora"
                    className="text-orange-600 hover:underline"
                  >
                    Septic Installation in Kenora
                  </Link>
                </li>
                <li>
                  <Link
                    to="/service-areas"
                    className="text-orange-600 hover:underline"
                  >
                    View All Service Areas
                  </Link>
                </li>
              </ul>
            </div>

            <div className="bg-orange-50 border border-orange-100 rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--dark-navy)" }}>
                Ready to Talk to an Installer?
              </h2>
              <p className="text-gray-700 mb-4">
                Use the information centre to research your options, then
                request a free estimate or use the calculator when you are
                ready.
              </p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/contact"
                  className="inline-block px-5 py-3 rounded-md font-semibold text-white text-center hover:opacity-90"
                  style={{ backgroundColor: "var(--primary-orange)" }}
                >
                  Request Free Estimate
                </Link>
                <Link
                  to="/septic-calculator"
                  className="inline-block px-5 py-3 rounded-md font-semibold text-orange-600 text-center border border-orange-500 hover:bg-orange-50"
                >
                  Use Septic Cost Calculator
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InformationCentrePage;
